import { menuItems } from "../../../json/SideBarItems.js";
import SidebarSection from "./SidebarSection.jsx";
import { Bot, Menu, Pin, PinOff } from "lucide-react";
import { useState, useRef, useEffect } from "react";

function mergeClasses(...classes) {
  return classes.filter(Boolean).join(" ");
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function Sidebar() {
  const apiGroups = `${backendUrl}/api/getAllGroups`;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const sidebarRef = useRef(null);

  const userEmail = localStorage.getItem("email");
  const userRole = localStorage.getItem("role");
  const [filteredMenuItems, setFilteredMenuItems] = useState(menuItems);

  useEffect(() => {
    async function fetchGroups() {
      try {
        const res = await fetch(apiGroups);
        const data = await res.json();

        const userGroups = data.filter((group) =>
          group.users.some((user) => user.email === userEmail)
        );

        if (userRole === "Authenticated") {
          setFilteredMenuItems(menuItems);
          return;
        }

        const boardsToFilter = [
          "MASTER DESIGN",
          "COMPAO PRODUCT",
          "RRSS",
          "COMPAO BRANDS",
        ];
        const userGroupNames = userGroups.map((g) => g.name.toUpperCase());

        const filtered = menuItems.map((section) => {
          const filteredItems = section.item.filter((item) => {
            const itemTitle = item.title.toUpperCase();

            if (boardsToFilter.includes(itemTitle)) {
              return userGroupNames.includes(itemTitle);
            }

            return true;
          });

          return {
            ...section,
            item: filteredItems,
          };
        });

        setFilteredMenuItems(filtered);
      } catch (err) {
        console.error("Error fetching groups:", err);
        setFilteredMenuItems(menuItems);
      }
    }

    if (userEmail) {
      fetchGroups();
    } else {
      setFilteredMenuItems(menuItems);
    }
  }, [userEmail]);

  useEffect(() => {
    const storedPinnedState = localStorage.getItem("sidebarPinned");
    if (storedPinnedState !== null) {
      const pinned = storedPinnedState === "true";
      setIsPinned(pinned);
      if (pinned) setIsCollapsed(false);
    }
  }, []);

  const togglePin = () => {
    const newPinnedState = !isPinned;
    setIsPinned(newPinnedState);
    localStorage.setItem("sidebarPinned", newPinnedState);
    if (newPinnedState) {
      setIsCollapsed(false);
    }
  };

  const toggleCollapse = () => {
    if (!isPinned) setIsCollapsed(!isCollapsed);
  };

  const onExpand = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
  };

  return (
    <aside
      ref={sidebarRef}
      className={mergeClasses(
        "h-screen flex flex-col transition-all duration-300 ease-in-out relative",
        "bg-[#F8F7FD] border-r border-slate-200",
        isCollapsed ? "w-auto" : "w-66"
      )}
    >
      <div className="p-4 border-b border-slate-200 h-[73px] flex">
        <div className="flex items-center justify-between w-[100%]">
          {!isCollapsed && (
            <img src="/ionvo.png" alt="IONVO group" className="h-8" />
          )}
          <div className="flex items-center gap-2">
            {!isCollapsed && (
              <button
                onClick={togglePin}
                className={mergeClasses(
                  "p-2 rounded-lg transition-all duration-300",
                  "hover:bg-slate-100 text-slate-600",
                  "active:scale-95",
                  isPinned && "text-gray-500"
                )}
                title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
              >
                {isPinned ? (
                  <PinOff className="h-4 w-4" />
                ) : (
                  <Pin className="h-4 w-4" />
                )}
              </button>
            )}
            <button
              onClick={toggleCollapse}
              className={mergeClasses(
                "rounded-lg transition-all duration-300",
                "hover:bg-slate-100 text-slate-600",
                "active:scale-95",
                isCollapsed && "w-full flex justify-center"
              )}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-200">
        <div className="space-y-0.5 py-2">
          {filteredMenuItems.map((section, index) => (
            <SidebarSection
              key={index}
              section={section}
              isCollapsed={isCollapsed}
              onExpand={onExpand}
            />
          ))}
        </div>
      </nav>
      <a href="/assistant" className="flex flex-col items-center">
        <Bot className="w-6 h-6 text-violet-500" size={30} />
        <span className="text-[12px] tracking-wide text-slate-900">
          Kala IA
        </span>
      </a>
    </aside>
  );
}
